import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Input, InputNumber, Select, message, Space, Popconfirm, Tag, Typography, Card, AutoComplete, Modal, Form, Row, Col, Tabs, Switch, Breadcrumb } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, DollarOutlined, TeamOutlined, ShopOutlined, FileExcelOutlined, ArrowLeftOutlined, CalendarOutlined, RightOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Text } = Typography;

const STATUS = [
  { value: 'Unpaid', label: 'Unpaid' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Advance', label: 'Advance' },
  { value: 'Partial', label: 'Partial' },
];

const EMPTY = {
  client_name: '', channel_name: '', channel_url: '', channel_id: '', cms_company: '', vendor: '', month: '',
  revenue_usd: 0, payment_status: 'Unpaid', payment_date: '', advance_inr: 0, advance_date: '', notes: '',
};

const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 'YYYY-MM' -> 'Jan 26'
const monthLabel = (m) => {
  const mt = /^(\d{4})-(\d{2})/.exec(m || '');
  if (!mt) return m || '';
  return `${MONTH_NAMES[Number(mt[2]) - 1] || mt[2]} ${mt[1].slice(2)}`;
};
const csvEsc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;

// CMS import puts channels with no client here — not a real client.
const UNASSIGNED = 'Unassigned (client set nahi)';

// Payment entry kinds: advance bhi client ko diya paisa hai, isliye balance me minus hota hai.
const PAY_KIND = {
  paid: { label: 'Paid', color: 'green' },
  advance: { label: 'Advance', color: 'orange' },
  received: { label: 'Received', color: 'cyan' },
};

function syncWarning(sync) {
  const values = [sync?.revenue, sync?.sheet, ...Object.values(sync?.revenue || {}), ...Object.values(sync?.sheet || {})];
  return values.find(v => typeof v === 'string' && v.startsWith('error:')) || '';
}

function downloadCsv(filename, lines) {
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function ClientChannels() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState({});   // id -> changed row
  const [savingId, setSavingId] = useState(null);
  const [clientNames, setClientNames] = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [monthStatus, setMonthStatus] = useState({});      // "client|month" -> {received, paid, ...}
  const [statusSaving, setStatusSaving] = useState('');
  const [openClient, setOpenClient] = useState(null);      // client drill-down
  const [openMonth, setOpenMonth] = useState(null);        // month drill-down inside a client
  // Hisab: per-client CMS cut %, per-month editable totals, irregular payments
  const [cuts, setCuts] = useState({});                    // client -> cut %
  const [hisabMonths, setHisabMonths] = useState({});      // "client|month" -> {total_inr, ...}
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm] = Form.useForm();
  const [inrDraft, setInrDraft] = useState({});            // "client|month" -> typed INR

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/client-channels');
      setRows(res.data.rows || []);
      setDirty({});
    } catch (err) { message.error(err.response?.data?.error || 'Load failed'); }
    finally { setLoading(false); }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const res = await api.get('/clients');
      setClientNames((res.data.clients || []).map(c => c.name).filter(Boolean));
    } catch (err) { /* clients list is optional here */ }
  }, []);

  const loadMonthStatus = useCallback(async () => {
    try {
      const res = await api.get('/client-channels/month-status');
      const map = {};
      (res.data.rows || []).forEach(r => { map[`${r.client_name}|${r.month}`] = r; });
      setMonthStatus(map);
    } catch (err) { /* month status is optional */ }
  }, []);

  const loadHisab = useCallback(async () => {
    try {
      const res = await api.get('/client-channels/hisab');
      const cutMap = {};
      (res.data.settings || []).forEach(s => { cutMap[s.client_name] = Number(s.cms_cut_percent || 0); });
      const monthMap = {};
      (res.data.months || []).forEach(m => { monthMap[`${m.client_name}|${m.month}`] = m; });
      setCuts(cutMap);
      setHisabMonths(monthMap);
      setPayments(res.data.payments || []);
    } catch (err) { /* hisab is optional */ }
  }, []);

  useEffect(() => { load(); loadClients(); loadMonthStatus(); loadHisab(); }, [load, loadClients, loadMonthStatus, loadHisab]);

  const saveCut = async (clientName, value) => {
    try {
      await api.post('/client-channels/hisab-settings', { client_name: clientName, cms_cut_percent: Number(value || 0) });
      setCuts(prev => ({ ...prev, [clientName]: Number(value || 0) }));
      message.success(`${clientName}: CMS cut ${Number(value || 0)}% set`);
    } catch (err) { message.error(err.response?.data?.error || 'Cut save failed'); }
  };

  const saveMonthInr = async (clientName, month, value) => {
    try {
      await api.post('/client-channels/hisab-month', { client_name: clientName, month, total_inr: value === null || value === undefined ? '' : value });
      await loadHisab();
      message.success(`${monthLabel(month)}: INR total save ho gaya`);
    } catch (err) { message.error(err.response?.data?.error || 'INR save failed'); }
  };

  const submitPayment = async (v) => {
    try {
      await api.post('/client-channels/hisab-payment', {
        client_name: openClient,
        direction: v.direction,
        month: v.month || '',
        pay_date: v.pay_date || '',
        amount_usd: v.amount_usd || 0,
        amount_inr: v.amount_inr || 0,
        note: v.note || '',
      });
      message.success('Payment entry add ho gayi');
      setPayOpen(false);
      payForm.resetFields();
      loadHisab();
    } catch (err) { message.error(err.response?.data?.error || 'Payment save failed'); }
  };

  const deletePayment = async (id) => {
    try { await api.delete(`/client-channels/hisab-payment/${id}`); message.success('Entry hata di'); loadHisab(); }
    catch (err) { message.error(err.response?.data?.error || 'Delete failed'); }
  };

  // Client Payments tab stays mounted alongside this one, so a client added there
  // must push its new name into this tab's list instead of waiting for a reload.
  useEffect(() => {
    window.addEventListener('clients-updated', loadClients);
    return () => window.removeEventListener('clients-updated', loadClients);
  }, [loadClients]);

  const toggleMonthStatus = async (clientName, month, field, value) => {
    const key = `${clientName}|${month}`;
    setStatusSaving(`${key}|${field}`);
    try {
      await api.post('/client-channels/month-status', { client_name: clientName, month, field, value: value ? 1 : 0 });
      setMonthStatus(prev => ({
        ...prev,
        [key]: { ...(prev[key] || { client_name: clientName, month }), [field]: value ? 1 : 0, [`${field}_date`]: value ? new Date().toISOString().slice(0, 10) : '' }
      }));
      message.success(`${month}: ${field === 'paid' ? 'Paid' : 'Received'} ${value ? 'ho gaya' : 'hata diya'}`);
      if (field === 'paid') load();
    } catch (err) { message.error(err.response?.data?.error || 'Update failed'); }
    finally { setStatusSaving(''); }
  };

  const setCell = (id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    setDirty(prev => ({ ...prev, [id]: true }));
  };

  const saveRow = async (row) => {
    setSavingId(row.id);
    try {
      const res = await api.put(`/client-channels/${row.id}`, row);
      const warning = syncWarning(res.data.sync);
      if (warning) message.warning(`Saved, but automatic sync failed: ${warning.slice(7)}`);
      else message.success('Saved and synced to Google Sheet');
      setDirty(prev => { const n = { ...prev }; delete n[row.id]; return n; });
    } catch (err) { message.error(err.response?.data?.error || 'Save failed'); }
    finally { setSavingId(null); }
  };

  const addRow = async () => {
    try {
      const res = await api.post('/client-channels', EMPTY);
      const warning = syncWarning(res.data.sync);
      if (warning) message.warning(`Row added, but automatic sync failed: ${warning.slice(7)}`);
      else message.success('Row added and synced to Google Sheet');
      load();
    } catch (err) { message.error(err.response?.data?.error || 'Add failed'); }
  };

  const openAdd = () => {
    loadClients();
    addForm.resetFields();
    addForm.setFieldsValue({ ...EMPTY, client_name: filterClient || undefined });
    setAddOpen(true);
  };

  const submitAdd = async (v) => {
    try {
      const res = await api.post('/client-channels', { ...EMPTY, ...v });
      const warning = syncWarning(res.data.sync);
      if (warning) message.warning(`Channel added, but automatic sync failed: ${warning.slice(7)}`);
      else message.success('Channel added and synced to Google Sheet');
      const claimed = Number(res.data.claimed_months || 0);
      if (claimed) message.success(`${claimed} purane mahine (Jan se) is client ke hisab me aa gaye`);
      setAddOpen(false);
      load();
      loadClients();
      loadMonthStatus();
    } catch (err) { message.error(err.response?.data?.error || 'Add failed'); }
  };

  const deleteRow = async (id) => {
    try {
      const res = await api.delete(`/client-channels/${id}`);
      const warning = syncWarning(res.data.sync);
      if (warning) message.warning(`Deleted, but automatic sync failed: ${warning.slice(7)}`);
      else message.success('Deleted and synced to Google Sheet');
      load();
    }
    catch (err) { message.error(err.response?.data?.error || 'Delete failed'); }
  };

  const txt = (field, width = 150) => (_, r) => (
    <Input size="small" style={{ width }} value={r[field]}
      onChange={e => setCell(r.id, field, e.target.value)} />
  );
  const dateCell = (field, width = 130) => (_, r) => (
    <Input size="small" type="date" style={{ width }} value={r[field] || ''}
      onChange={e => setCell(r.id, field, e.target.value)} />
  );
  const money = (field, symbol = '$') => (_, r) => (
    <InputNumber size="small" style={{ width: 120 }} value={r[field]} min={0}
      onChange={v => setCell(r.id, field, v ?? 0)} prefix={symbol} />
  );

  const clientCell = (_, r) => (
    <AutoComplete size="small" style={{ width: 150 }} value={r.client_name}
      options={allClients.map(n => ({ value: n }))}
      filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
      onChange={v => setCell(r.id, 'client_name', v)} placeholder="Client" />
  );

  const columns = [
    { title: 'Client', dataIndex: 'client_name', fixed: 'left', width: 150, render: clientCell },
    { title: 'Channel', dataIndex: 'channel_name', width: 160, render: txt('channel_name', 160) },
    { title: 'Channel Link', dataIndex: 'channel_url', width: 190, render: txt('channel_url', 190) },
    { title: 'Channel ID', dataIndex: 'channel_id', width: 140, render: txt('channel_id', 140) },
    { title: 'CMS / Company', dataIndex: 'cms_company', width: 150, render: txt('cms_company') },
    { title: 'Vendor', dataIndex: 'vendor', width: 150, render: txt('vendor') },
    { title: 'Month', dataIndex: 'month', width: 130, render: (_, r) => (
      <Input size="small" type="month" style={{ width: 130 }} value={r.month || ''}
        onChange={e => setCell(r.id, 'month', e.target.value)} />
    ) },
    { title: 'Revenue (USD)', dataIndex: 'revenue_usd', width: 120, render: money('revenue_usd', '$') },
    {
      title: 'Payment', dataIndex: 'payment_status', width: 120,
      render: (_, r) => (
        <Select size="small" style={{ width: 110 }} value={r.payment_status}
          options={STATUS} onChange={v => setCell(r.id, 'payment_status', v)} />
      )
    },
    { title: 'Payment Date', dataIndex: 'payment_date', width: 140, render: dateCell('payment_date', 140) },
    { title: 'Advance (₹)', dataIndex: 'advance_inr', width: 120, render: money('advance_inr', '₹') },
    { title: 'Advance Date', dataIndex: 'advance_date', width: 140, render: dateCell('advance_date', 140) },
    { title: 'Notes', dataIndex: 'notes', width: 160, render: txt('notes', 160) },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" type="primary" icon={<SaveOutlined />}
            disabled={!dirty[r.id]} loading={savingId === r.id}
            onClick={() => saveRow(r)} />
          <Popconfirm title="Delete this row?" onConfirm={() => deleteRow(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    },
  ];

  const shownRows = filterClient ? rows.filter(r => (r.client_name || '') === filterClient) : rows;
  const totalRev = shownRows.reduce((s, r) => s + Number(r.revenue_usd || 0), 0);
  const unpaid = shownRows.filter(r => r.payment_status !== 'Paid').reduce((s, r) => s + Number(r.revenue_usd || 0), 0);
  const allClients = Array.from(new Set([...clientNames, ...rows.map(r => r.client_name).filter(Boolean)]));

  // ---- Group helpers for Client Hisab / Vendor Hisab ----
  const groupBy = (key) => {
    const map = {};
    rows.forEach(r => {
      const k = (r[key] || '').trim() || UNASSIGNED;
      if (!map[k]) map[k] = { name: k, rows: [], revenue_usd: 0, advance_inr: 0, paid_usd: 0, pending_usd: 0, channels: new Set(), paid_till: '' };
      const g = map[k];
      g.rows.push(r);
      g.revenue_usd += Number(r.revenue_usd || 0);
      g.advance_inr += Number(r.advance_inr || 0);
      if (r.payment_status === 'Paid') {
        g.paid_usd += Number(r.revenue_usd || 0);
        if ((r.month || '') > g.paid_till) g.paid_till = r.month || '';
      } else {
        g.pending_usd += Number(r.revenue_usd || 0);
      }
      if (r.channel_name) g.channels.add(r.channel_name);
    });
    return Object.values(map)
      .map(g => ({ ...g, channel_count: g.channels.size }))
      .sort((a, b) => b.revenue_usd - a.revenue_usd);
  };
  const clientGroups = useMemo(() => groupBy('client_name'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const vendorGroups = useMemo(() => groupBy('vendor'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Month-wise pivot (Jan itne, Feb itne ... + side me Paid/Unpaid) ----
  const allMonths = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { if (r.month) s.add(r.month); });
    return Array.from(s).sort();
  }, [rows]);

  // For a group: per-month revenue + combined status (Paid/Unpaid/Partial).
  const withMonthBreakdown = (groups) => groups.map(g => {
    const byMonth = {};
    g.rows.forEach(r => {
      const m = r.month || '';
      if (!m) return;
      if (!byMonth[m]) byMonth[m] = { rev: 0, paid: 0, unpaid: 0 };
      byMonth[m].rev += Number(r.revenue_usd || 0);
      if (r.payment_status === 'Paid') byMonth[m].paid += 1; else byMonth[m].unpaid += 1;
    });
    Object.values(byMonth).forEach(c => {
      c.status = c.paid && c.unpaid ? 'Partial' : (c.paid ? 'Paid' : 'Unpaid');
    });
    return { ...g, byMonth };
  });
  const clientPivot = useMemo(() => {
    const groups = withMonthBreakdown(clientGroups);
    // A freshly created client has no channel row yet — still list it so it can be
    // opened and filled from here.
    const known = new Set(groups.map(g => g.name));
    const empties = clientNames.filter(n => n && !known.has(n)).map(n => ({
      name: n, rows: [], revenue_usd: 0, advance_inr: 0, paid_usd: 0, pending_usd: 0,
      channels: new Set(), channel_count: 0, paid_till: '', byMonth: {},
    }));
    return [...groups, ...empties];
  }, [clientGroups, clientNames]); // eslint-disable-line react-hooks/exhaustive-deps
  const vendorPivot = useMemo(() => withMonthBreakdown(vendorGroups), [vendorGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // kind: 'client' -> Paid/Unpaid ; 'vendor' -> Received/Unreceived
  const pivotColumns = (label, icon, kind) => {
    const statusLabel = (s) => kind === 'vendor'
      ? (s === 'Paid' ? 'Received' : s === 'Unpaid' ? 'Unreceived' : 'Partial')
      : (s === 'Paid' ? 'Paid' : s === 'Unpaid' ? 'Unpaid' : 'Partial');
    const statusColor = (s) => s === 'Paid' ? 'green' : s === 'Unpaid' ? 'red' : 'gold';
    return [
      { title: label, dataIndex: 'name', fixed: 'left', width: 170, render: (n) => <Space>{icon}<Text strong>{n}</Text></Space> },
      ...allMonths.map(m => ({
        title: monthLabel(m), dataIndex: ['byMonth', m], width: 120, align: 'center',
        render: (_, g) => {
          const c = g.byMonth[m];
          if (!c) return <Text type="secondary">—</Text>;
          return (
            <div style={{ lineHeight: 1.3 }}>
              <div><Text strong>{usd(c.rev)}</Text></div>
              <Tag color={statusColor(c.status)} style={{ marginRight: 0 }}>{statusLabel(c.status)}</Tag>
            </div>
          );
        }
      })),
      { title: 'Total', dataIndex: 'revenue_usd', fixed: 'right', width: 120, align: 'right', render: (v) => <Text strong>{usd(v)}</Text> },
      { title: kind === 'vendor' ? 'Received' : 'Paid', dataIndex: 'paid_usd', fixed: 'right', width: 110, align: 'right', render: (v) => <Text type="secondary">{usd(v)}</Text> },
      { title: kind === 'vendor' ? 'Pending' : 'Unpaid', dataIndex: 'pending_usd', fixed: 'right', width: 110, align: 'right', render: (v) => <Text style={{ color: v > 0 ? '#cf1322' : '#3f8600' }}>{usd(v)}</Text> },
    ];
  };

  const assignClient = async (row, value) => {
    const name = (value || '').trim();
    const current = (rows.find(r => r.id === row.id)?.client_name || '').trim();
    if (!name || name === current) return;
    await saveRow({ ...row, client_name: name });
    load();
  };

  // Only shown for the "Unassigned" group: pick a client and the row moves there.
  const assignClientColumn = {
    title: 'Client set karo', key: 'assign', width: 190, fixed: 'left',
    render: (_, r) => (
      <AutoComplete size="small" style={{ width: 175 }} placeholder="Client choose/type"
        options={allClients.filter(Boolean).map(n => ({ value: n }))}
        filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
        disabled={savingId === r.id}
        onSelect={(v) => assignClient(r, v)}
        onBlur={(e) => assignClient(r, e.target.value)} />
    )
  };

  // cutFor: client name -> % we deduct, so every channel row shows the same
  // after-cut amount that the month/client totals use.
  const detailColumns = (showClient, cutClient = '') => {
    const cut = Number(cuts[cutClient] || 0);
    return [
    ...(showClient ? [{ title: 'Client', dataIndex: 'client_name', width: 130 }] : []),
    { title: 'Channel', dataIndex: 'channel_name', width: 150 },
    { title: 'CMS / Company', dataIndex: 'cms_company', width: 130 },
    { title: 'Vendor', dataIndex: 'vendor', width: 120 },
    { title: 'Month', dataIndex: 'month', width: 100 },
    { title: 'Revenue (100%)', dataIndex: 'revenue_usd', width: 120, align: 'right', render: (v) => usd(v) },
    ...(cutClient ? [
      { title: `Hamara cut (${cut}%)`, key: 'ch_cut', width: 120, align: 'right', render: (_, r) => <Text type="secondary">{usd(Number(r.revenue_usd || 0) * cut / 100)}</Text> },
      { title: 'Client ko dena', key: 'ch_payable', width: 130, align: 'right', render: (_, r) => <Text strong style={{ color: '#5566d6' }}>{usd(Number(r.revenue_usd || 0) * (1 - cut / 100))}</Text> },
    ] : []),
    { title: 'Payment', dataIndex: 'payment_status', width: 90, render: (s) => <Tag color={s === 'Paid' ? 'green' : s === 'Unpaid' ? 'red' : 'gold'}>{s}</Tag> },
    { title: 'Pay Date', dataIndex: 'payment_date', width: 110 },
    { title: 'Advance (₹)', dataIndex: 'advance_inr', width: 110, align: 'right', render: (v) => inr(v) },
    { title: 'Adv Date', dataIndex: 'advance_date', width: 110 },
    ];
  };

  // Vendor-wise grouped CSV: each vendor block, then 4 blank lines, then next vendor.
  const exportVendorWise = () => {
    const L = [];
    L.push([ 'Client Channels — Vendor-wise Hisab' ].map(csvEsc).join(','));
    L.push('');
    vendorGroups.forEach((g) => {
      L.push([`VENDOR: ${g.name}`, `${g.channel_count} channels`, `Revenue ${usd(g.revenue_usd)}`, `Advance ${inr(g.advance_inr)}`].map(csvEsc).join(','));
      L.push(['Client', 'Channel', 'Channel Link', 'CMS / Company', 'Month', 'Revenue (USD)', 'Payment', 'Payment Date', 'Advance (INR)', 'Advance Date'].map(csvEsc).join(','));
      g.rows.forEach((r) => L.push([
        r.client_name, r.channel_name, r.channel_url, r.cms_company, r.month,
        r.revenue_usd, r.payment_status, r.payment_date, r.advance_inr, r.advance_date
      ].map(csvEsc).join(',')));
      L.push(''); L.push(''); L.push(''); L.push('');
    });
    downloadCsv('client-channels-vendor-wise.csv', L);
  };

  // One month of one client -> its own sheet (that month's channel-wise revenue).
  const exportClientMonth = (clientName, month, monthRows, st = {}) => {
    const cut = Number(cuts[clientName] || 0);
    const gross = monthRows.reduce((s, r) => s + Number(r.revenue_usd || 0), 0);
    const L = [];
    L.push([`${clientName} — ${monthLabel(month)}`].map(csvEsc).join(','));
    L.push([
      `Channels: ${monthRows.length}`,
      `Revenue (100%): ${usd(gross)}`,
      `Hamara cut (${cut}%): ${usd(gross * cut / 100)}`,
      `Client ko dena: ${usd(gross * (1 - cut / 100))}`,
      `Paid: ${st.paid ? `Yes${st.paid_date ? ` (${st.paid_date})` : ''}` : 'No'}`,
      `Received: ${st.received ? `Yes${st.received_date ? ` (${st.received_date})` : ''}` : 'No'}`,
    ].map(csvEsc).join(','));
    L.push('');
    L.push(['Channel', 'Channel Link', 'CMS / Company', 'Vendor', 'Month', 'Revenue (USD, 100%)', `Hamara cut (${cut}%)`, 'Client ko dena (USD)', 'Payment', 'Payment Date', 'Advance (INR)', 'Advance Date'].map(csvEsc).join(','));
    monthRows.forEach((r) => L.push([
      r.channel_name, r.channel_url, r.cms_company, r.vendor, r.month, r.revenue_usd,
      (Number(r.revenue_usd || 0) * cut / 100).toFixed(2), (Number(r.revenue_usd || 0) * (1 - cut / 100)).toFixed(2),
      r.payment_status, r.payment_date, r.advance_inr, r.advance_date
    ].map(csvEsc).join(',')));
    downloadCsv(`${clientName}-${monthLabel(month).replace(/ /g, '-')}.csv`, L);
  };

  const exportClientStatement = (g) => {
    const L = [];
    L.push([`Client Statement — ${g.name}`].map(csvEsc).join(','));
    L.push([`Channels: ${g.channel_count}`, `Total Revenue: ${usd(g.revenue_usd)}`, `Paid: ${usd(g.paid_usd)}`, `Pending: ${usd(g.pending_usd)}`, `Advance: ${inr(g.advance_inr)}`].map(csvEsc).join(','));
    L.push('');
    L.push(['Channel', 'CMS / Company', 'Vendor', 'Month', 'Revenue (USD)', 'Payment', 'Payment Date', 'Advance (INR)', 'Advance Date'].map(csvEsc).join(','));
    g.rows.forEach((r) => L.push([
      r.channel_name, r.cms_company, r.vendor, r.month, r.revenue_usd,
      r.payment_status, r.payment_date, r.advance_inr, r.advance_date
    ].map(csvEsc).join(',')));
    downloadCsv(`${g.name}-statement.csv`, L);
  };

  const ledgerTab = (
    <div>
      <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
        <Space size="large" wrap>
          <span><DollarOutlined /> <Text strong>Total Revenue:</Text> <Tag color="blue">{usd(totalRev)}</Tag></span>
          <span><Text strong>Unpaid:</Text> <Tag color="red">{usd(unpaid)}</Tag></span>
          <span><Text type="secondary">{shownRows.length} rows</Text></span>
        </Space>
      </Card>

      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Channel</Button>
        <Button icon={<PlusOutlined />} onClick={addRow}>Add Blank Row</Button>
        <Button onClick={load}>Refresh</Button>
        <Select size="middle" style={{ width: 200 }} allowClear placeholder="Filter by Client"
          value={filterClient || undefined} onChange={v => setFilterClient(v || '')}
          options={allClients.map(n => ({ value: n, label: n }))} showSearch />
        <Text type="secondary">Client, channel, CMS aur vendor Save karte hi Google Sheet "Client Channels" tab automatic update hota hai. Revenue CMS se Channel ID ke through aata hai.</Text>
      </Space>

      <Table size="small" rowKey="id" loading={loading} columns={columns}
        dataSource={shownRows} pagination={{ pageSize: 50 }} scroll={{ x: 2000 }} />
    </div>
  );

  // Client -> months -> that month's channels. Nothing else opens, so a client
  // never dumps every month's channel rows on screen at once.
  const clientGroup = openClient ? clientPivot.find(g => g.name === openClient) : null;

  const clientMonthRows = useMemo(() => {
    if (!clientGroup) return [];
    return Object.keys(clientGroup.byMonth).sort().map(m => {
      const st = monthStatus[`${clientGroup.name}|${m}`] || {};
      const monthRows = clientGroup.rows.filter(r => (r.month || '') === m);
      return {
        month: m,
        revenue_usd: clientGroup.byMonth[m].rev,
        channels: monthRows.length,
        received: Number(st.received || 0) === 1,
        received_date: st.received_date || '',
        paid: Number(st.paid || 0) === 1 || clientGroup.byMonth[m].status === 'Paid',
        paid_date: st.paid_date || '',
        rows: monthRows,
      };
    });
  }, [clientGroup, monthStatus]);

  const monthDetailRows = openMonth ? (clientMonthRows.find(m => m.month === openMonth)?.rows || []) : [];

  const clientListTable = (
    <Table size="small" rowKey="name" loading={loading} pagination={false}
      dataSource={clientPivot}
      scroll={{ x: 1200 }}
      onRow={(g) => ({ style: { cursor: 'pointer' }, onClick: () => { setOpenClient(g.name); setOpenMonth(null); } })}
      columns={[
        { title: 'Client', dataIndex: 'name', render: (n) => <Space><TeamOutlined /><Text strong>{n}</Text></Space> },
        { title: 'Months', key: 'months', width: 100, align: 'center', render: (_, g) => Object.keys(g.byMonth).length },
        { title: 'Channels', dataIndex: 'channel_count', width: 100, align: 'center' },
        { title: 'Total Revenue (100%)', dataIndex: 'revenue_usd', width: 150, align: 'right', render: (v) => <Text strong>{usd(v)}</Text> },
        {
          title: 'Revenue share % (hamara cut, 30 = client ko 70%)', key: 'cut', width: 170, align: 'center',
          render: (_, g) => (
            <InputNumber size="small" min={0} max={100} style={{ width: 80 }}
              value={cuts[g.name] ?? 0}
              onClick={(e) => e.stopPropagation()}
              onChange={(v) => setCuts(prev => ({ ...prev, [g.name]: Number(v || 0) }))}
              onBlur={(e) => saveCut(g.name, e.target.value)} />
          )
        },
        {
          title: 'Client ko dena', key: 'payable', width: 140, align: 'right',
          render: (_, g) => <Text strong style={{ color: '#5566d6' }}>{usd(Number(g.revenue_usd || 0) * (1 - (cuts[g.name] || 0) / 100))}</Text>
        },
        { title: 'Paid', dataIndex: 'paid_usd', width: 120, align: 'right', render: (v) => <Text type="secondary">{usd(v)}</Text> },
        { title: 'Unpaid', dataIndex: 'pending_usd', width: 120, align: 'right', render: (v) => <Text style={{ color: v > 0 ? '#cf1322' : '#3f8600' }}>{usd(v)}</Text> },
        {
          title: '', key: 'go', width: 60, align: 'center',
          render: () => <RightOutlined style={{ color: '#8c8c8c' }} />
        },
      ]}
    />
  );

  const monthListTable = clientGroup && (
    <Table size="small" rowKey="month" pagination={false} dataSource={clientMonthRows}
      columns={[
        {
          title: 'Month', dataIndex: 'month', width: 160,
          render: (m) => (
            <Button type="link" style={{ padding: 0 }} onClick={() => setOpenMonth(m)}>
              <Space><CalendarOutlined /><Text strong>{monthLabel(m)}</Text></Space>
            </Button>
          )
        },
        { title: 'Revenue (100%)', dataIndex: 'revenue_usd', width: 130, align: 'right', render: (v) => <Text strong>{usd(v)}</Text> },
        {
          title: `Client ko dena (cut ${cuts[clientGroup.name] || 0}%)`, key: 'payable', width: 150, align: 'right',
          render: (_, r) => <Text strong style={{ color: '#5566d6' }}>{usd(Number(r.revenue_usd || 0) * (1 - (cuts[clientGroup.name] || 0) / 100))}</Text>
        },
        {
          title: 'Total INR (edit karo)', key: 'inr', width: 170, align: 'right',
          render: (_, r) => {
            const key = `${clientGroup.name}|${r.month}`;
            const saved = hisabMonths[key];
            const value = inrDraft[key] !== undefined
              ? inrDraft[key]
              : (saved && Number(saved.total_inr_set) === 1 ? Number(saved.total_inr) : null);
            return (
              <InputNumber size="small" style={{ width: 150 }} min={0} placeholder="jo asli me aaya"
                value={value}
                formatter={(v) => (v === '' || v === null || v === undefined ? '' : `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
                parser={(v) => String(v || '').replace(/[^0-9.]/g, '')}
                onChange={(v) => setInrDraft(prev => ({ ...prev, [key]: v }))}
                onBlur={() => saveMonthInr(clientGroup.name, r.month, inrDraft[key] === undefined ? null : inrDraft[key])} />
            );
          }
        },
        {
          title: 'Paid', key: 'paid', width: 150, align: 'center',
          render: (_, r) => (
            <Space direction="vertical" size={0}>
              <Switch size="small" checked={r.paid}
                loading={statusSaving === `${clientGroup.name}|${r.month}|paid`}
                checkedChildren="Paid" unCheckedChildren="Unpaid"
                onChange={(v) => toggleMonthStatus(clientGroup.name, r.month, 'paid', v)} />
              {r.paid && r.paid_date ? <Text type="secondary" style={{ fontSize: 11 }}>{r.paid_date}</Text> : null}
            </Space>
          )
        },
        {
          title: 'Received', key: 'received', width: 150, align: 'center',
          render: (_, r) => (
            <Space direction="vertical" size={0}>
              <Switch size="small" checked={r.received}
                loading={statusSaving === `${clientGroup.name}|${r.month}|received`}
                checkedChildren="Received" unCheckedChildren="Pending"
                onChange={(v) => toggleMonthStatus(clientGroup.name, r.month, 'received', v)} />
              {r.received && r.received_date ? <Text type="secondary" style={{ fontSize: 11 }}>{r.received_date}</Text> : null}
            </Space>
          )
        },
        { title: 'Channels', dataIndex: 'channels', width: 100, align: 'center' },
        {
          title: 'Channels dekho', key: 'open', width: 240, align: 'center',
          render: (_, r) => (
            <Space>
              <Button size="small" onClick={() => setOpenMonth(r.month)}>Open {monthLabel(r.month)}</Button>
              <Button size="small" icon={<FileExcelOutlined />}
                onClick={() => exportClientMonth(clientGroup.name, r.month, r.rows, r)}>Excel</Button>
            </Space>
          )
        },
      ]}
    />
  );

  const monthDetailTable = clientGroup && openMonth && (
    <div>
      <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
        <Space size="large" wrap>
          <Text strong>{clientGroup.name} — {monthLabel(openMonth)}</Text>
          <span><Text strong>Channels:</Text> {monthDetailRows.length}</span>
          <span><Text strong>Revenue (100%):</Text> <Tag color="blue">{usd(monthDetailRows.reduce((s, r) => s + Number(r.revenue_usd || 0), 0))}</Tag></span>
          <span><Text strong>Hamara cut ({Number(cuts[clientGroup.name] || 0)}%):</Text> <Tag color="purple">{usd(monthDetailRows.reduce((s, r) => s + Number(r.revenue_usd || 0), 0) * Number(cuts[clientGroup.name] || 0) / 100)}</Tag></span>
          <span><Text strong>Client ko dena:</Text> <Tag color="geekblue">{usd(monthDetailRows.reduce((s, r) => s + Number(r.revenue_usd || 0), 0) * (1 - Number(cuts[clientGroup.name] || 0) / 100))}</Tag></span>
          <Button size="small" icon={<FileExcelOutlined />}
            onClick={() => exportClientMonth(clientGroup.name, openMonth, monthDetailRows,
              clientMonthRows.find(m => m.month === openMonth) || {})}>
            Download {monthLabel(openMonth)} Excel
          </Button>
        </Space>
      </Card>
      {clientGroup.name === UNASSIGNED && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Ye CMS revenue import se aaye channels hain jinka client set nahi hai. Neeche "Client set karo" se
          jis client ka channel hai wo choose kar do — row us client ke hisab me chali jayegi.
        </Text>
      )}
      <Table size="small" rowKey="id" pagination={false} dataSource={monthDetailRows}
        columns={clientGroup.name === UNASSIGNED
          ? [assignClientColumn, ...detailColumns(false)]
          : detailColumns(false, clientGroup.name)} scroll={{ x: 1300 }} />
    </div>
  );

  // Irregular payments for the open client: kab kitna diya / aaya + balance.
  const clientPayments = openClient ? payments.filter(p => p.client_name === openClient) : [];
  const payTotals = clientPayments.reduce((a, p) => {
    const k = ['received', 'advance'].includes(p.direction) ? p.direction : 'paid';
    a[`${k}_inr`] += Number(p.amount_inr || 0);
    a[`${k}_usd`] += Number(p.amount_usd || 0);
    return a;
  }, { paid_inr: 0, paid_usd: 0, received_inr: 0, received_usd: 0, advance_inr: 0, advance_usd: 0 });
  const payableUsd = clientGroup ? Number(clientGroup.revenue_usd || 0) * (1 - (cuts[openClient] || 0) / 100) : 0;

  const paymentLedger = clientGroup && (
    <Card size="small" style={{ marginTop: 16, borderRadius: 12 }}
      title={<Space><DollarOutlined /><Text strong>{openClient} — Payment entries (beech-beech me di gayi payment)</Text></Space>}
      extra={<Button size="small" type="primary" icon={<PlusOutlined />}
        onClick={() => { payForm.resetFields(); payForm.setFieldsValue({ direction: 'paid', pay_date: new Date().toISOString().slice(0, 10) }); setPayOpen(true); }}>
        Add Payment / Advance
      </Button>}
    >
      <Space size="large" wrap style={{ marginBottom: 12 }}>
        <span><Text strong>Client ko dena (cut ke baad):</Text> <Tag color="blue">{usd(payableUsd)}</Tag></span>
        <span><Text strong>Advance liya:</Text> <Tag color="orange">{inr(payTotals.advance_inr)}{payTotals.advance_usd ? ` / ${usd(payTotals.advance_usd)}` : ''}</Tag></span>
        <span><Text strong>Diya (Paid):</Text> <Tag color="green">{inr(payTotals.paid_inr)}{payTotals.paid_usd ? ` / ${usd(payTotals.paid_usd)}` : ''}</Tag></span>
        <span><Text strong>Received (hamein aaya):</Text> <Tag color="cyan">{inr(payTotals.received_inr)}{payTotals.received_usd ? ` / ${usd(payTotals.received_usd)}` : ''}</Tag></span>
        <span><Text strong>Baki ($ me):</Text> <Tag color={payableUsd - payTotals.paid_usd - payTotals.advance_usd > 0 ? 'red' : 'green'}>{usd(payableUsd - payTotals.paid_usd - payTotals.advance_usd)}</Tag></span>
      </Space>
      <Table size="small" rowKey="id" pagination={{ pageSize: 10 }} dataSource={clientPayments}
        locale={{ emptyText: 'Abhi koi payment entry nahi — "Add Payment" se jab bhi payment ho tab daal do.' }}
        columns={[
          { title: 'Date', dataIndex: 'pay_date', width: 110 },
          { title: 'Kya hua', dataIndex: 'direction', width: 110, render: (d) => <Tag color={PAY_KIND[d]?.color || 'green'}>{PAY_KIND[d]?.label || 'Paid'}</Tag> },
          { title: 'Month', dataIndex: 'month', width: 100, render: (m) => m ? monthLabel(m) : '—' },
          { title: 'Amount (₹)', dataIndex: 'amount_inr', width: 130, align: 'right', render: (v) => inr(v) },
          { title: 'Amount ($)', dataIndex: 'amount_usd', width: 120, align: 'right', render: (v) => Number(v) ? usd(v) : '—' },
          { title: 'Rate (₹/$)', dataIndex: 'usd_inr_rate', width: 100, align: 'right', render: (v) => Number(v) ? Number(v).toFixed(2) : '—' },
          { title: 'Note', dataIndex: 'note' },
          {
            title: '', key: 'act', width: 60,
            render: (_, p) => (
              <Popconfirm title="Ye galat entry hata deni hai?" onConfirm={() => deletePayment(p.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )
          },
        ]}
        scroll={{ x: 900 }}
      />
    </Card>
  );

  const clientHisabTab = (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Breadcrumb items={[
          { title: openClient ? <Button type="link" style={{ padding: 0 }} onClick={() => { setOpenClient(null); setOpenMonth(null); }}>Clients</Button> : 'Clients' },
          ...(openClient ? [{ title: openMonth ? <Button type="link" style={{ padding: 0 }} onClick={() => setOpenMonth(null)}>{openClient}</Button> : openClient }] : []),
          ...(openMonth ? [{ title: monthLabel(openMonth) }] : []),
        ]} />
        {openClient && (
          <Button size="small" icon={<ArrowLeftOutlined />}
            onClick={() => (openMonth ? setOpenMonth(null) : setOpenClient(null))}>Back</Button>
        )}
        {openClient && !openMonth && clientGroup && (
          <Button size="small" icon={<FileExcelOutlined />} onClick={() => exportClientStatement(clientGroup)}>Statement Excel</Button>
        )}
        <Text type="secondary">
          {!openClient
            ? 'Client par click karo — sirf uske mahine (Jan, Feb, Mar…) khulenge.'
            : !openMonth
              ? 'Har month ke saamne Paid aur Received tick. Month par click karne par usi month ke channels khulenge; Excel button se sirf usi month ka data download hoga.'
              : 'Sirf is month ke channels aur revenue — upar wale Excel button se yahi month download hoga.'}
        </Text>
      </Space>
      {!openClient ? clientListTable : (openMonth ? monthDetailTable : monthListTable)}
      {openClient && !openMonth && clientGroup && paymentLedger}
    </div>
  );

  const vendorHisabTab = (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<FileExcelOutlined />} onClick={exportVendorWise}>Download Vendor-wise Sheet</Button>
        <Text type="secondary">Har vendor ka month-wise hisab (Jan, Feb, Mar…) — har month ke neeche revenue + Received/Unreceived. Row expand karo to us vendor ke channel-wise details.</Text>
      </Space>
      <Table size="small" rowKey="name" loading={loading} pagination={false}
        columns={pivotColumns('Vendor', <ShopOutlined />, 'vendor')}
        dataSource={vendorPivot}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (g) => (
            <Table size="small" rowKey="id" columns={detailColumns(true)} dataSource={g.rows}
              pagination={false} scroll={{ x: 1100 }} />
          )
        }}
      />
    </div>
  );

  return (
    <div>
      <Tabs
        items={[
          { key: 'ledger', label: 'Channel Ledger', children: ledgerTab },
          { key: 'client', label: <span><TeamOutlined /> Client Hisab</span>, children: clientHisabTab },
          { key: 'vendor', label: <span><ShopOutlined /> Vendor Hisab</span>, children: vendorHisabTab },
        ]}
      />

      <Modal title={`Payment entry — ${openClient || ''}`} open={payOpen} onCancel={() => setPayOpen(false)} onOk={() => payForm.submit()} okText="Save">
        <Form form={payForm} layout="vertical" onFinish={submitPayment}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="direction" label="Kya hua" initialValue="paid" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'paid', label: 'Paid (client ko diya)' },
                  { value: 'advance', label: 'Advance (client ne advance liya)' },
                  { value: 'received', label: 'Received (hamein aaya)' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}><Form.Item name="pay_date" label="Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="amount_inr" label="Amount (₹)"><InputNumber min={0} style={{ width: '100%' }} prefix="₹" /></Form.Item></Col>
            <Col span={12}><Form.Item name="amount_usd" label="Amount ($) — optional"><InputNumber min={0} style={{ width: '100%' }} prefix="$" /></Form.Item></Col>
          </Row>
          <Form.Item name="month" label="Kis month ka (optional — khali chhodo to general)"><Input type="month" /></Form.Item>
          <Form.Item name="note" label="Note"><Input.TextArea rows={2} placeholder="e.g. Dec-Feb ka payment" /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Add Channel" open={addOpen} onCancel={() => setAddOpen(false)} onOk={() => addForm.submit()} okText="Add">
        <Form form={addForm} layout="vertical" onFinish={submitAdd}>
          <Form.Item name="client_name" label="Client (kis client ka channel hai)" rules={[{ required: true, message: 'Client choose karo' }]}>
            <AutoComplete options={allClients.map(n => ({ value: n }))}
              filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Client choose ya type karo" />
          </Form.Item>
          <Form.Item name="channel_name" label="Channel Name" rules={[{ required: true }]}><Input placeholder="e.g. Vinay Bhakti" /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="channel_url" label="Channel Link"><Input placeholder="https://youtube.com/@..." /></Form.Item></Col>
            <Col span={12}><Form.Item name="channel_id" label="YouTube Channel ID" extra="CMS API se auto-revenue ke liye (UCxxxx)"><Input placeholder="UCxxxxxxxx" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="cms_company" label="CMS / Company"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="vendor" label="Vendor"><Input placeholder="Payment kaun karta hai" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="month" label="Month"><Input type="month" /></Form.Item></Col>
            <Col span={8}><Form.Item name="revenue_usd" label="Revenue (USD)"><InputNumber min={0} style={{ width: '100%' }} prefix="$" /></Form.Item></Col>
            <Col span={8}><Form.Item name="payment_status" label="Payment" initialValue="Unpaid"><Select options={STATUS} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="payment_date" label="Payment Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="advance_inr" label="Advance (₹)"><InputNumber min={0} style={{ width: '100%' }} prefix="₹" /></Form.Item></Col>
            <Col span={8}><Form.Item name="advance_date" label="Advance Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
